import Checkbox from '@mui/material/Checkbox'
import DialogContent from '@mui/material/DialogContent'
import FormControlLabel from '@mui/material/FormControlLabel'
import { DataEditContextTextField } from 'Components/reusable/DataContext/fields/DataEditContextTextField'
import { DataEditContextNumberField } from 'Components/reusable/DataContext/fields/DataEditContextNumberField'
import { DataEditContextDateField } from 'Components/reusable/DataContext/fields/DataEditContextDateField'
import { DataEditContextAssignmentField } from 'Components/reusable/DataContext/fields/DataEditContextAssignmentField'
import { DataEditContextTypeField } from 'Components/reusable/DataContext/fields/DataEditContextTypeField'
import { DataEditContextRoleField } from 'Components/reusable/DataContext/fields/DataEditContextRoleField'
import { DataEditContextStateField } from 'Components/reusable/DataContext/fields/DataEditContextStateField'
import { DataEditContextTranslationImport } from 'Components/reusable/DataContext/TranslationImport'
import Grid from '@mui/material/Grid2'
import { allocationAPI } from 'api/AllocationAPI'
import { assignmentAPI } from 'api/AssignmentAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import { useNotificationPopup } from 'Components/reusable/Notification'
import newWithEditModal from 'Components/reusable/HOC/newWithEditModal'
import { NewOrExisting } from 'Components/reusable/DataContext'
import { assignmentAllocationSchema, ownAllocationSchema } from 'Components/reusable/DataContext/ValidationSchema'
import { useUser } from 'hooks/user'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { IAllocationScopeItem } from 'types/allocationInterfaces'
import { IAllocation, IAssignment, IAssignmentRole } from 'types/assignmentInterfaces'
import { IError } from 'types/error'
import { allocationStates } from 'utils/utils'
import PersonPicker from 'Components/reusable/InputFields/PersonPicker'
import { NetworkId, OrganizationId, TeamId } from 'types/ids'
import { IPerson } from 'types/userInterfaces'
import AllocationScopePicker from 'Components/reusable/InputFields/AllocationScopePicker'
import { ObjectSchema } from 'yup'

/** @notExported */
interface IComponentProps {
  /** Function to be called when the modal is closed. */
  onClose: ({ newItem }: { newItem?: IAllocation }) => void
  /** The item to be edited. */
  item?: IAllocation | null
  /** The id of the person. */
  personId: number
  /** The data of the item. */
  data: IAllocation
  /** Function to be called when the data is changed. */
  setData: React.Dispatch<React.SetStateAction<NewOrExisting<IAllocation> | null>>
  /** Submit indicator. */
  submitIndicator: boolean
  /** Function to reset the submit indicator. */
  resetSubmit: () => void
  /** Function to set the schema. */
  setSchema: React.Dispatch<React.SetStateAction<ObjectSchema<object | undefined, object> | undefined>>
  /** Whether the modal is for management. */
  management?: boolean
  /** The scope options. */
  scopeOptions: IAllocationScopeItem[]
  /** Does the user have sales access */
  salesAccess?: boolean
}

/**
 * Allocation modal.
 *
 * @returns Allocation modal component.
 * @notExported
 */
const AllocationModal: React.FC<IComponentProps> = ({
  onClose,
  item,
  personId,
  data,
  setData,
  submitIndicator,
  resetSubmit,
  setSchema,
  management,
  scopeOptions,
  salesAccess,
}) => {
  const { t, i18n } = useTranslation()
  const { setSuccessNotificationPopup } = useNotificationPopup()
  const states = allocationStates()
  const { user, groups } = useUser()

  const [backendError, setBackendError] = useState<IError>()
  const [assignments, setAssignments] = useState<IAssignment[]>([])
  const [assignmentRoles, setAssignmentRoles] = useState<IAssignmentRole[]>([])
  const [showNames, setShowNames] = useState<boolean>(false)
  const [selectedUser, setSelectedUser] = useState<IPerson>()
  const [selectedScope, setSelectedScope] = useState<{ value: number; label: string; type: string }>()

  if (item && !item.id) {
    personId = item.personId
  }

  const types: { type: string; label: string; info?: React.ReactElement }[] = useMemo(() => {
    const items: { type: string; label: string; info?: React.ReactElement }[] = []
    if ((user && user.Person && user.Person.id === personId) || salesAccess) {
      items.push({
        type: 'personal',
        label: i18n.t('allocation.own'),
        info: (
          <Trans i18nKey="allocation.own.info">
            Use <strong>own</strong> allocation to mark vacations, absences or dedicated time for own purposes.
          </Trans>
        ),
      })
    }

    if (groups && (groups.includes('admin') || groups.includes('sales'))) {
      items.push({
        type: 'project',
        label: i18n.t('allocation.project'),
      })
    }

    if (groups && (groups.includes('sales') || groups.includes('admin') || groups.includes('teamLeader'))) {
      items.push({
        type: 'other',
        label: i18n.t('allocation.other'),
        info: (
          <Trans i18nKey="allocation.other.info">
            Use <strong>quick assignments</strong> to do lightweight resourcing.
          </Trans>
        ),
      })
    }

    return items
  }, [groups, i18n, personId, salesAccess, user])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (data.assignmentId) {
        try {
          if (groups && (groups.includes('sales') || groups.includes('admin'))) {
            if (!data.startDate && !data.endDate && data.Assignment) {
              setData({
                ...data,
                startDate: data.Assignment.startDate,
                endDate: data.Assignment.endDate ? data.Assignment.endDate : data.endDate,
              })
            }

            const results = await assignmentAPI.getAssignmentRoles(data.assignmentId, controller)
            setAssignmentRoles(results)
          }
        } catch (error) {
          setBackendError(error as IError)
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [data, data.assignmentId, groups, setData])

  useEffect(() => {
    const controller = new AbortController()
    setShowNames(false)

    if (data.type === 'project' && assignments.length < 1) {
      ;(async () => {
        try {
          if (groups && (groups.includes('sales') || groups.includes('admin'))) {
            const results = await assignmentAPI.getAssignmentList(controller)
            setAssignments(results)
          }
        } catch (error) {
          setBackendError(error as IError)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [assignments.length, data, data.type, groups, item, setData])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (submitIndicator === true) {
        try {
          let newItem: IAllocation
          if (item && item.id) {
            if (data.type !== 'project') {
              newItem = await allocationAPI.saveOther(data as IAllocation, controller)
            } else {
              newItem = await allocationAPI.save(data as IAllocation, controller)
            }
          } else {
            if (selectedScope && selectedUser) {
              if (data.type !== 'project') {
                newItem = await allocationAPI.createOther(
                  {
                    ...data,
                    personId: selectedUser.id,
                  },
                  controller
                )
              } else {
                newItem = await allocationAPI.create(
                  {
                    ...data,
                    personId: selectedUser.id,
                  },
                  controller
                )
              }
            } else {
              if (data.type !== 'project') {
                newItem = await allocationAPI.createOther(
                  {
                    ...data,
                    personId: personId,
                  },
                  controller
                )
              } else {
                newItem = await allocationAPI.create(
                  {
                    ...data,
                    personId: personId,
                  },
                  controller
                )
              }
            }
          }
          onClose({ newItem })
          setSuccessNotificationPopup()
        } catch (err) {
          setBackendError(err as IError)
          resetSubmit()
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [
    data,
    item,
    onClose,
    personId,
    resetSubmit,
    selectedScope,
    selectedUser,
    setSuccessNotificationPopup,
    submitIndicator,
  ])

  useEffect(() => {
    if (data.type !== 'project') {
      setSchema(ownAllocationSchema())
    } else {
      setSchema(assignmentAllocationSchema())
    }
  }, [data.type, setSchema])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <DialogContent sx={{ backgroundColor: 'white' }}>
      <DataEditContextTranslationImport<IAllocation> />
      {!item && management && (
        <AllocationScopePicker
          id="allocationScope"
          value={selectedScope}
          onChange={scope => setSelectedScope(scope)}
          items={scopeOptions || []}
          label={t('allocation.networkScope')}
          required
          insetLabel
        />
      )}
      {!item && management && selectedScope && (
        <PersonPicker
          id="allocationPerson"
          value={selectedUser ?? null}
          organizationId={
            user && selectedScope && selectedScope.type === 'own-company'
              ? (user.organizationId as OrganizationId)
              : selectedScope && selectedScope.type === 'company'
                ? (selectedScope.value as OrganizationId)
                : undefined
          }
          teamId={selectedScope && selectedScope.type === 'team' ? (selectedScope.value as TeamId) : undefined}
          networkId={selectedScope && selectedScope.type === 'network' ? (selectedScope.value as NetworkId) : undefined}
          onChange={newValue => {
            if (newValue) {
              setSelectedUser(newValue)
            }
          }}
          insetLabel
          required
        />
      )}
      {(!item || (item && !item.id)) && (
        <DataEditContextTypeField<IAllocation>
          id="AllocationType"
          field="type"
          typeOptions={types}
          required
          disabled={!item && management && (!selectedUser || !selectedScope)}
          insetLabel
        />
      )}
      {data && data.type === 'project' && assignments.length > 0 && (
        <>
          <DataEditContextAssignmentField<IAllocation>
            id="allocationAssignment"
            field="assignmentId"
            dataField="Assignment"
            assignmentOptions={assignments}
            required
            disabled={!item && management && (!selectedUser || !selectedScope)}
            insetLabel
          />
          {data.assignmentId && assignmentRoles && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    id="allocationShowNames"
                    onChange={e => {
                      e.stopPropagation()
                      setShowNames(!showNames)
                    }}
                    checked={showNames}
                  />
                }
                label={t(`allocation.showAllocatedRoles`)}
                disabled={!item && management && (!selectedUser || !selectedScope)}
              />
              <DataEditContextRoleField<IAllocation>
                id="allocationRole"
                field="roleId"
                dataField="AssignmentRole"
                assignmentRoles={assignmentRoles}
                showNames={showNames}
                required
                disabled={!item && management && (!selectedUser || !selectedScope)}
                insetLabel
              />
            </>
          )}
        </>
      )}
      <DataEditContextStateField<IAllocation>
        id="allocationState"
        field="state"
        stateOptions={states}
        required
        disabled={!item && management && (!selectedUser || !selectedScope)}
        insetLabel
      />
      <DataEditContextNumberField<IAllocation>
        id="allocationPercent"
        field="percent"
        required
        fullWidth
        disabled={!item && management && (!selectedUser || !selectedScope)}
        insetLabel
      />
      <Grid container spacing={2}>
        <Grid size={6}>
          <DataEditContextDateField<IAllocation>
            id="allocationStartDate"
            field="startDate"
            required
            dateResolution="day"
            start={true}
            disabled={!item && management && (!selectedUser || !selectedScope)}
            insetLabel
          />
        </Grid>
        <Grid size={6}>
          <DataEditContextDateField<IAllocation>
            id="allocationEndDate"
            field="endDate"
            required
            dateResolution="day"
            end={true}
            minDate={data.startDate || null}
            disabled={!item && management && (!selectedUser || !selectedScope)}
            insetLabel
          />
        </Grid>
      </Grid>
      <DataEditContextTextField<IAllocation>
        id="allocationInformation"
        field="information"
        multiline
        grow
        rows={6}
        fullWidth
        disabled={!item && management && (!selectedUser || !selectedScope)}
        insetLabel
      />
    </DialogContent>
  )
}

export default newWithEditModal(AllocationModal)
